/** Frozen contract — settings spine panes import these shapes in phases 3+4. */

import { SEED_USER } from './user-seed';

export type UsagePeriodDays = 7 | 30 | 90;

export type UsageModelRow = {
  model: string;
  kit: string;
  costUsd: number;
  turns: number;
  inputTokens: number;
  outputTokens: number;
};

export type UsageDailyRow = {
  date: string;
  costUsd: number;
  turns: number;
};

export type UsageConnectorRow = {
  connectorId: string;
  name: string;
  invocations: number;
};

export type UsageSummarySeed = {
  sinceDays: UsagePeriodDays;
  totalCostUsd: number;
  chatCostUsd: number;
  chatTurns: number;
  inputTokens: number;
  outputTokens: number;
  agentCostUsd: number;
  agentRuns: number;
  byModel: UsageModelRow[];
  daily: UsageDailyRow[];
  connectors: {
    totalInvocations: number;
    byConnector: UsageConnectorRow[];
  };
  embeddings: {
    costUsd: number;
    calls: number;
    inputTokens: number;
  };
};

export const MEMORY_TYPES = ['user', 'feedback', 'project', 'reference'] as const;
export type MemoryType = (typeof MEMORY_TYPES)[number];

export type SettingsMemory = {
  id: string;
  content: string;
  createdAt: number;
  lastReadAt: number;
  type: MemoryType;
};

export type ConnectorMcpStatus =
  | 'connected'
  | 'pending'
  | 'needs-auth'
  | 'failed'
  | 'disabled';

export type InstalledConnectorSeed = {
  id: string;
  marketplaceId: string;
  name: string;
  description: string;
  enabled: boolean;
  mcpStatus: ConnectorMcpStatus;
  needsReauth: boolean;
  invocations: number;
  installedAt: number;
  transport: string;
  logoUrl?: string;
  logoInvertInDark?: boolean;
  builtin?: boolean;
};

export type MarketplaceConnectorSeed = {
  id: string;
  name: string;
  description: string;
  category: string;
  publisher: string;
  installed: boolean;
  transport: string;
  logoUrl?: string;
  logoInvertInDark?: boolean;
};

export type SettingsSkillSeed = {
  slug: string;
  description: string;
  source: 'user' | 'platform';
  updatedAt: number;
};

export type PluginSkillSeed = {
  pluginId: string;
  pluginName: string;
  name: string;
  description: string;
};

export type InstalledPluginSeed = {
  id: string;
  marketplaceId: string;
  name: string;
  version: string;
  description: string;
  enabled: boolean;
  trusted: boolean;
  installedAt: number;
  publisher: string;
};

export type MarketplacePluginSeed = {
  id: string;
  name: string;
  description: string;
  publisher: string;
  version: string;
  installed: boolean;
  tools?: string[];
};

export type LinkedIdentitySeed = {
  id: string;
  provider: string;
  linkedAt: number;
  lastSignInAt: number;
};

export type SkillBodySeed = {
  slug: string;
  body: string;
};

export type PluginSkillBodySeed = {
  pluginId: string;
  name: string;
  body: string;
  blockedReason?: string;
};

export type PluginMarketplaceSeed = {
  id: string;
  name: string | null;
  source: 'github' | 'url';
  url: string;
};

export type SharedChatSeed = {
  id: string;
  title: string;
  sharedWith: string;
  createdAt: number;
};

const day = 86_400_000;
const now = Date.UTC(2026, 6, 15);

function buildUsageDailySeries(count: number, endAt: number): UsageDailyRow[] {
  const rows: UsageDailyRow[] = [];
  for (let i = count - 1; i >= 0; i -= 1) {
    const date = new Date(endAt - i * day);
    const iso = date.toISOString().slice(0, 10);
    const wave = 0.55 + 0.45 * Math.sin(i / 4.5);
    const costUsd = Number((0.35 + wave * 2.85 + (i % 7) * 0.08).toFixed(2));
    const turns = Math.max(1, Math.round(3 + wave * 16 + (i % 5)));
    rows.push({ date: iso, costUsd, turns });
  }
  return rows;
}

export const SEED_USAGE_SUMMARY: UsageSummarySeed = {
  sinceDays: 30,
  totalCostUsd: 18.42,
  chatCostUsd: 14.86,
  chatTurns: 128,
  inputTokens: 412_500,
  outputTokens: 98_200,
  agentCostUsd: 3.56,
  agentRuns: 17,
  byModel: [
    {
      model: 'claude-sonnet-4',
      kit: 'claude',
      costUsd: 9.12,
      turns: 72,
      inputTokens: 248_000,
      outputTokens: 54_100,
    },
    {
      model: 'claude-haiku-4',
      kit: 'claude',
      costUsd: 2.44,
      turns: 38,
      inputTokens: 96_500,
      outputTokens: 28_400,
    },
    {
      model: 'gemini-2.5-pro',
      kit: 'google',
      costUsd: 3.3,
      turns: 18,
      inputTokens: 68_000,
      outputTokens: 15_700,
    },
  ],
  daily: buildUsageDailySeries(90, now),
  connectors: {
    totalInvocations: 156,
    byConnector: [
      { connectorId: 'github', name: 'GitHub', invocations: 68 },
      { connectorId: 'notion', name: 'Notion', invocations: 42 },
      { connectorId: 'knowledge-base', name: 'Knowledge Base', invocations: 46 },
    ],
  },
  embeddings: {
    costUsd: 1.24,
    calls: 1247,
    inputTokens: 892_000,
  },
};

export const SEED_SETTINGS_MEMORIES: SettingsMemory[] = [
  {
    id: 'mem-1',
    content: `${SEED_USER.name} prefers concise bullet summaries in status updates.`,
    createdAt: now - 20 * day,
    lastReadAt: now - 2 * day,
    type: 'user',
  },
  {
    id: 'mem-2',
    content: 'Use Pacific/Auckland timezone when scheduling follow-ups.',
    createdAt: now - 12 * day,
    lastReadAt: now - 4 * day,
    type: 'reference',
  },
  {
    id: 'mem-3',
    content: 'Foundation mock ships with seeded admin and settings panes only.',
    createdAt: now - 8 * day,
    lastReadAt: now - 1 * day,
    type: 'project',
  },
  {
    id: 'mem-4',
    content: 'Avoid suggesting deployment steps until the client signs off on UI parity.',
    createdAt: now - 5 * day,
    lastReadAt: now - 5 * day,
    type: 'feedback',
  },
];

export const SEED_INSTALLED_CONNECTORS: InstalledConnectorSeed[] = [
  {
    id: 'user-conn-knowledge-base',
    marketplaceId: 'knowledge-base',
    name: 'Knowledge Base',
    description: 'Built-in retrieval over your uploaded files.',
    enabled: true,
    mcpStatus: 'connected',
    needsReauth: false,
    invocations: 156,
    installedAt: now - 90 * day,
    transport: 'http',
    logoUrl: '/connector-logos/knowledge-base.svg',
    logoInvertInDark: true,
    builtin: true,
  },
  {
    id: 'user-conn-github',
    marketplaceId: 'github',
    name: 'GitHub',
    description: 'Issues, pull requests, and repository context.',
    enabled: true,
    mcpStatus: 'connected',
    needsReauth: false,
    invocations: 42,
    installedAt: now - 45 * day,
    transport: 'http',
    logoUrl: '/connector-logos/github.svg',
    logoInvertInDark: true,
  },
  {
    id: 'user-conn-notion',
    marketplaceId: 'notion',
    name: 'Notion',
    description: 'Workspace pages and databases.',
    enabled: true,
    mcpStatus: 'needs-auth',
    needsReauth: true,
    invocations: 11,
    installedAt: now - 30 * day,
    transport: 'http',
    logoUrl: '/connector-logos/notion.svg',
    logoInvertInDark: true,
  },
  {
    id: 'user-conn-linear',
    marketplaceId: 'linear',
    name: 'Linear',
    description: 'Issue tracking and project updates.',
    enabled: false,
    mcpStatus: 'disabled',
    needsReauth: false,
    invocations: 0,
    installedAt: now - 10 * day,
    transport: 'http',
    logoUrl: '/connector-logos/linear.svg',
  },
];

export const SEED_MARKETPLACE_CONNECTORS: MarketplaceConnectorSeed[] = [
  {
    id: 'granola',
    name: 'Granola',
    description: 'Meeting notes and action items.',
    category: 'Productivity',
    publisher: 'Granola',
    installed: false,
    transport: 'http',
  },
  {
    id: 'slack',
    name: 'Slack',
    description: 'Channels, threads, and workspace search.',
    category: 'Communication',
    publisher: 'Slack',
    installed: false,
    transport: 'http',
  },
  {
    id: 'google-drive',
    name: 'Google Drive',
    description: 'Files and shared drives.',
    category: 'Storage',
    publisher: 'Google',
    installed: false,
    transport: 'http',
  },
  {
    id: 'jira',
    name: 'Jira',
    description: 'Issues, sprints, and boards.',
    category: 'Project management',
    publisher: 'Atlassian',
    installed: false,
    transport: 'http',
  },
];

export const SEED_INSTALLED_PLUGINS: InstalledPluginSeed[] = [
  {
    id: 'plugin-atlassian',
    marketplaceId: 'atlassian',
    name: 'Atlassian',
    version: '1.2.0',
    description: 'Jira and Confluence tools for issue triage and doc lookup.',
    enabled: true,
    trusted: true,
    installedAt: now - 60 * day,
    publisher: 'Atlassian',
  },
  {
    id: 'plugin-linear-tools',
    marketplaceId: 'linear-tools',
    name: 'Linear Tools',
    version: '0.9.1',
    description: 'Create and update Linear issues from chat.',
    enabled: false,
    trusted: false,
    installedAt: now - 14 * day,
    publisher: 'Community',
  },
];

export const SEED_PLUGIN_MARKETPLACES: PluginMarketplaceSeed[] = [
  {
    id: 'claude-plugins-official',
    name: 'Anthropic official plugins',
    source: 'github',
    url: 'https://github.com/anthropics/claude-plugins-official',
  },
];

export const SEED_MARKETPLACE_PLUGINS: MarketplacePluginSeed[] = [
  {
    id: 'atlassian',
    name: 'Atlassian',
    description: 'Jira and Confluence tools for issue triage and doc lookup.',
    publisher: 'Atlassian',
    version: '1.2.0',
    installed: true,
    tools: ['jira_search', 'jira_create_issue', 'confluence_search', 'confluence_get_page'],
  },
  {
    id: 'linear-tools',
    name: 'Linear Tools',
    description: 'Create and update Linear issues from chat.',
    publisher: 'Community',
    version: '0.9.1',
    installed: true,
    tools: ['linear_list_issues', 'linear_create_issue', 'linear_update_issue'],
  },
  {
    id: 'salesforce',
    name: 'Salesforce',
    description: 'CRM lookups and opportunity updates.',
    publisher: 'Salesforce',
    version: '2.0.0',
    installed: false,
    tools: ['salesforce_query', 'salesforce_get_opportunity', 'salesforce_update_opportunity'],
  },
  {
    id: 'snowflake',
    name: 'Snowflake',
    description: 'Run warehouse queries and inspect schemas.',
    publisher: 'Snowflake',
    version: '1.0.3',
    installed: false,
    tools: ['snowflake_run_query', 'snowflake_list_tables', 'snowflake_describe_table'],
  },
];

export const SEED_LINKED_IDENTITIES: LinkedIdentitySeed[] = [
  {
    id: 'li-microsoft',
    provider: 'microsoft-entra-id',
    linkedAt: now - 120 * day,
    lastSignInAt: now - 1 * day,
  },
];

export const SEED_SKILL_BODIES: SkillBodySeed[] = [
  {
    slug: 'weekly-status',
    body: `# Weekly status

Summarise the week's highlights into a one-page status update.

## Inputs
- Thread highlights or pasted notes
- Optional project name

## Steps
1. Group updates by theme (shipped, in progress, blocked).
2. Lead with outcomes, not activity.
3. End with next-week priorities (max 3 bullets).`,
  },
  {
    slug: 'meeting-minutes',
    body: `# Meeting minutes

Turn rough notes into structured minutes with owners.

- Capture decisions explicitly
- Assign an owner to every action item
- Keep tone neutral and factual`,
  },
  {
    slug: 'release-notes',
    body: `# Release notes

Summarise shipped changes for client-facing release notes.

Focus on user-visible impact. Skip internal refactors unless they unblock a client ask.`,
  },
  {
    slug: 'code-review',
    body: `# Code review

Review a diff for correctness, tests, and style.

Check edge cases, error handling, and whether tests cover the change.`,
  },
  {
    slug: 'doc-writer',
    body: `# Doc writer

Expand bullet outlines into polished documentation.

Prefer short paragraphs, active voice, and concrete examples.`,
  },
  {
    slug: 'data-viz-brief',
    body: `# Data viz brief

Propose chart types and labels for a dataset summary.

Name the audience, the question the chart answers, and one alternative if the data is sparse.`,
  },
  {
    slug: 'email-polish',
    body: `# Email polish

Rewrite drafts for clarity and a professional tone.

Preserve intent; tighten openings and sign-offs.`,
  },
];

export const SEED_PLUGIN_SKILL_BODIES: PluginSkillBodySeed[] = [
  {
    pluginId: 'atlassian',
    name: 'jira-triage',
    body: `# Jira triage

Triage incoming Jira issues with priority hints.

## When to use
- A batch of new issues needs sorting
- The user asks for priority recommendations

## Steps
1. Read issue title, description, and labels.
2. Suggest priority (P0–P3) with a one-line rationale.
3. Flag missing fields (assignee, component, repro steps).`,
    blockedReason:
      'Plugin skills are read-only in this mock — edit the plugin bundle or create a personal copy.',
  },
];

export const SEED_PLUGIN_SKILLS: PluginSkillSeed[] = [
  {
    pluginId: 'atlassian',
    pluginName: 'atlassian',
    name: 'jira-triage',
    description: 'Triage incoming Jira issues with priority hints.',
  },
];

export const SEED_SHARED_CHATS: SharedChatSeed[] = [
  {
    id: 'share-1',
    title: 'Q3 roadmap review',
    sharedWith: 'sam.patel@example.com',
    createdAt: now - 4 * day,
  },
  {
    id: 'share-2',
    title: 'Connector auth walkthrough',
    sharedWith: 'jordan.lee@example.com',
    createdAt: now - 9 * day,
  },
];

export const SEED_PERSONAL_SKILLS: SettingsSkillSeed[] = [
  {
    slug: 'weekly-status',
    description: 'Draft a one-page weekly status from thread highlights.',
    source: 'user',
    updatedAt: now - 6 * day,
  },
  {
    slug: 'meeting-minutes',
    description: 'Turn rough notes into structured minutes with owners.',
    source: 'user',
    updatedAt: now - 14 * day,
  },
  {
    slug: 'release-notes',
    description: 'Summarise shipped changes for client-facing release notes.',
    source: 'user',
    updatedAt: now - 21 * day,
  },
];

export const SEED_PLATFORM_SKILLS: SettingsSkillSeed[] = [
  {
    slug: 'code-review',
    description: 'Review a diff for correctness, tests, and style.',
    source: 'platform',
    updatedAt: now - 60 * day,
  },
  {
    slug: 'doc-writer',
    description: 'Expand bullet outlines into polished documentation.',
    source: 'platform',
    updatedAt: now - 60 * day,
  },
  {
    slug: 'data-viz-brief',
    description: 'Propose chart types and labels for a dataset summary.',
    source: 'platform',
    updatedAt: now - 45 * day,
  },
  {
    slug: 'email-polish',
    description: 'Rewrite drafts for clarity and a professional tone.',
    source: 'platform',
    updatedAt: now - 30 * day,
  },
];
